/**
 * 来場者セッションゲート画面
 * 保存済みセッションを確認して自動ログイン可否を判定します
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Text, View } from '../../../shared/components/WebPrimitives';
import { visitorRestoreSession } from '../services/visitorAuthService';
import {
  clearVisitorSession,
  loadVisitorSession,
  saveVisitorSession,
} from '../services/visitorSessionService';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import AppButton from '../../../shared/components/AppButton';
import ScreenLayout from '../../../shared/components/ScreenLayout';
import { logError } from '../../../shared/utils/logger';

/**
 * 来場者セッションゲート画面
 * @param {Object} props - プロパティ
 * @param {Object} props.navigation - ナビゲーション
 * @returns {JSX.Element} 来場者セッションゲート画面
 */
const VisitorSessionGateScreen = ({ navigation }) => {
  /** 復元確認の通信に失敗し、保存済みセッションを残したまま再試行を待っているか */
  const [isRestoreFailed, setIsRestoreFailed] = useState(false);

  /** 再試行で確認をやり直すための番号 */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    /** 画面離脱後に遷移・状態更新しないための生存フラグ */
    let isActive = true;

    /**
     * 保存済みセッションを検証して遷移先を決定する
     * 通信失敗は無効なセッションと区別し、端末のセッションを消さずに再試行を案内する。
     * @returns {Promise<void>}
     */
    const bootstrapVisitorSession = async () => {
      /** 保存済みセッション */
      let storedSession = null;

      try {
        storedSession = await loadVisitorSession();
      } catch (error) {
        logError('保存済み来場者セッションの読込に失敗しました。', {
          message: error instanceof Error ? error.message : String(error),
        });

        if (isActive) {
          navigation.replace(VISITOR_ROUTES.LOGIN, { sessionLost: false });
        }
        return;
      }

      if (!storedSession) {
        if (isActive) {
          navigation.replace(VISITOR_ROUTES.REGISTER);
        }
        return;
      }

      /** 復元結果 */
      let restoreResult = null;

      try {
        restoreResult = await visitorRestoreSession(storedSession.sessionToken);
      } catch (error) {
        // サーバーが無効と判定したわけではないため、セッションを消すとログインし直しになる。
        logError('来場者セッション復元に失敗しました。', {
          message: error instanceof Error ? error.message : String(error),
        });

        if (isActive) {
          setIsRestoreFailed(true);
        }
        return;
      }

      if (!isActive) {
        return;
      }

      if (!restoreResult.isValid) {
        try {
          await clearVisitorSession();
        } catch (clearError) {
          logError('来場者セッション削除に失敗しました。', {
            message: clearError instanceof Error ? clearError.message : String(clearError),
          });
        }

        navigation.replace(VISITOR_ROUTES.LOGIN, {
          sessionLost: restoreResult.isLost,
        });
        return;
      }

      /** 最新セッション情報 */
      const refreshedSession = {
        visitorId: restoreResult.visitorId,
        nickname: restoreResult.nickname,
        sessionToken: storedSession.sessionToken,
        sessionExpiresAt: restoreResult.sessionExpiresAt,
      };

      try {
        await saveVisitorSession(refreshedSession);
      } catch (saveError) {
        // 保存済みのセッションは有効なので、最新情報を書き戻せなくてもホームへ進める。
        logError('来場者セッションの更新保存に失敗しました。', {
          message: saveError instanceof Error ? saveError.message : String(saveError),
        });
      }

      if (isActive) {
        navigation.reset({
          index: 0,
          routes: [{ name: VISITOR_ROUTES.HOME }],
        });
      }
    };

    bootstrapVisitorSession();

    return () => {
      isActive = false;
    };
  }, [attempt, navigation]);

  /**
   * 保存済みセッションの確認をやり直す
   * @returns {void}
   */
  const handleRetry = useCallback(() => {
    setIsRestoreFailed(false);
    setAttempt((current) => current + 1);
  }, []);

  return (
    <ScreenLayout
      badge="冒険再開チェック"
      description="自動ログイン可否を確認しています。"
      mascotMood="focus"
      mascotPose="idle"
      theme="visitor"
      title="セッション確認"
    >
      {isRestoreFailed ? (
        <View className="gap-3 rounded-none border-2 border-app-border bg-[#FFF8DD] p-4">
          <Text accessibilityRole="alert" className="text-base text-app-text">
            ログイン状態を確認できませんでした。通信状況を確認して、もう一度お試しください。
          </Text>
          <AppButton label="もう一度確認する" onPress={handleRetry} />
        </View>
      ) : (
        <View className="rounded-none border-2 border-app-border bg-[#FFF8DD] p-4">
          <Text className="text-base text-app-text-secondary">保存済みセッションを確認中です...</Text>
        </View>
      )}
    </ScreenLayout>
  );
};

export default VisitorSessionGateScreen;
